'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Typography, AppImage, DotsIndicator } from '@workspace/ui/common';
import { processHeading } from '@workspace/lib/heading-utils';
import type { LogoWallProps, LogoWallSlide } from './logo-wall.types';
import { cn } from '@workspace/lib';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { LOGO_SIZE } from '@workspace/constants';
import { getBackgroundClass } from '@workspace/lib';
import { sanityImageSrc } from '@/sanity/lib/image';
import { getTextAlignClass } from './layout';

export function LogoWall({ block }: LogoWallProps) {
  const { backgroundColor } = block;
  const slidesFromCms: LogoWallSlide[] = useMemo(
    () => (Array.isArray(block?.slides) ? block.slides : []),
    [block?.slides],
  );

  // Autoplay plugin (kept in a ref so we can call .reset() on focus out)
  const autoplayRef = useRef(
    Autoplay({
      delay: 5000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: true,
      rootNode: (emblaRoot: HTMLElement) => emblaRoot?.parentElement as HTMLElement | null,
    }),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: slidesFromCms.length > 1,
      align: 'start',
      containScroll: 'keepSnaps',
      dragFree: false,
      slidesToScroll: 1,
    },
    slidesFromCms.length > 1 ? [autoplayRef.current] : [],
  );

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentSlide(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  // Resume autoplay when focus leaves the carousel area
  useEffect(() => {
    if (!emblaApi || !slidesFromCms.length) return;
    const root = emblaApi.rootNode?.() as HTMLElement | undefined;
    if (!root) return;
    const onFocusOut = () => {
      autoplayRef.current?.reset?.();
    };
    root.addEventListener('focusout', onFocusOut);
    return () => root.removeEventListener('focusout', onFocusOut);
  }, [emblaApi, slidesFromCms.length]);

  const goToSlide = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  if (!slidesFromCms.length) return null;

  const currentSlideData = slidesFromCms[currentSlide];
  const textAlignment = currentSlideData?.textAlignment;
  const textAlignClass = getTextAlignClass(textAlignment);
  const { text: resolvedHeadingText } = processHeading(currentSlideData.heading);
  const backgroundClass = getBackgroundClass(backgroundColor);

  return (
    <div className={cn('w-full', backgroundClass)}>
      <div className="porta-container flex flex-col items-center gap-6 md:gap-12 px-6 lg:pt-20 py-8 lg:px-30">
        {currentSlideData && (
          <div className={cn('flex flex-col gap-2 w-full', textAlignClass)}>
            <Typography variant="h2" styleVariant="h3" className={cn('font-bold ', textAlignClass)}>
              {resolvedHeadingText}
            </Typography>

            {Array.isArray(currentSlideData.description) && (
              <div className={cn('text-sm! leading-[1.42857] text-secondary-600', textAlignClass)}>
                <CustomPortableText value={currentSlideData.description} />
              </div>
            )}
          </div>
        )}

        {/* Logos Carousel */}
        <div className={cn('w-full')}>
          <div>
            <div className="embla w-full overflow-hidden" ref={emblaRef}>
              <div className="embla__container flex">
                {slidesFromCms.map((slide, index) => {
                  if (!slide.logos || !slide.logos.length) return null;
                  return (
                    <div key={index} className="embla__slide flex-[0_0_100%] min-w-0">
                      <div
                        className={cn(
                          'flex flex-wrap justify-center items-center gap-0 md:gap-sm w-full',
                          slide.logos.length <= 4 && 'justify-center',
                          slide.logos.length >= 5 && 'sm:justify-start lg:justify-center',
                        )}
                      >
                        {slide.logos.map((logo, logoIndex) => {
                          if (!logo?.url) return null;
                          return (
                            <div
                              key={logo._key || `${logo.url}-${logoIndex}`}
                              className="w-full md:w-auto flex justify-center items-center"
                            >
                              <div className="flex flex-col justify-center items-center gap-2 p-4 w-53.5 h-27.5 shrink-0">
                                <AppImage
                                  src={sanityImageSrc(logo.url, {
                                    width: LOGO_SIZE.width,
                                    height: LOGO_SIZE.height,
                                  })}
                                  alt={logo.alt || 'Logo'}
                                  width={LOGO_SIZE.width}
                                  height={LOGO_SIZE.height}
                                  className="w-full h-full object-contain"
                                  style={{
                                    objectFit: 'contain',
                                    objectPosition: 'center',
                                  }}
                                  loading="lazy"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Dots Navigation */}
            <DotsIndicator
              className="justify-center pt-md md:pt-xl"
              count={slidesFromCms.length}
              currentIndex={currentSlide}
              onDotClick={goToSlide}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LogoWall;
